package pl.decks.personal.components.widgets.hasdiagram

import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.*
import pl.decks.personal.styles.SiteLayout

object HASDiagramStylesheet {
    object Vars {
        val nodeSize by StyleVariable<CSSLengthOrPercentageNumericValue>(50.px)
        val innerNodeSize by StyleVariable(nodeSize.value())

        val nodeBackgroundColor by StyleVariable<Color>()
        val nodeLineWidth by StyleVariable<CSSLengthNumericValue>(SiteLayout.Line.Normal)
        val nodeLineStyle by StyleVariable(LineStyle.Solid)
        val nodeLineColor by StyleVariable<Color>()

        val connectingLineWidth by StyleVariable(nodeLineWidth.value())
        val connectingLineStyle by StyleVariable(nodeLineStyle.value())
        val connectingLineColor by StyleVariable<Color>()
        val guideLineWidth by StyleVariable(SiteLayout.Line.Hairline)
        val guideLineStyle by StyleVariable(LineStyle.Dashed)
        val guideLineColor by StyleVariable<Color>()
        object Internal {
            val nodeContentSize by StyleVariable<CSSLengthOrPercentageNumericValue>()
            val nodeOutlineColor by StyleVariable<Color>()

            val nodeStartingAngle by StyleVariable<CSSNumericValue<out CSSUnitAngle>>(0.deg)
            val nodeSweepAngle by StyleVariable<CSSNumericValue<out CSSUnitAngle>>(0.deg)
        }
    }

    object Diagram {
        val Container = CssStyle.base {
            Modifier
                .minWidth(Vars.nodeSize.value() * 2 + Vars.innerNodeSize.value())
                .aspectRatio(1)
        }
    }

    object Node {
        sealed interface Kind : ComponentKind
        val Back = CssStyle.base<Kind> {
            Modifier
                .position(Position.Relative)
                .borderRadius(SiteLayout.Rounding.Full)
                .padding(Vars.nodeLineWidth.value())
                .setVariable(Vars.Internal.nodeOutlineColor, Vars.nodeLineColor.value(colorMode.toPalette().border))
        }
        val Content = CssStyle.base {
            Modifier
                .minSize(Vars.Internal.nodeContentSize.value())
                .borderRadius(SiteLayout.Rounding.Full)
                .backgroundColor(Vars.nodeBackgroundColor.value(colorMode.toPalette().background))
                .outline(
                    width = Vars.nodeLineWidth.value(),
                    style = Vars.nodeLineStyle.value(),
                    color = Vars.Internal.nodeOutlineColor.value(),
                )
        }
        val GhostVariant = Back.addVariantBase {
            Modifier.setVariable(Vars.Internal.nodeOutlineColor, Colors.Transparent)
        }
        val PartialArcVariant = Back.addVariantBase {
            Modifier
                .setVariable(Vars.Internal.nodeOutlineColor, Colors.Transparent)
                .styleModifier {
                    property(
                        "background",
                        "conic-gradient(from var(--internal-node-starting-angle, 0deg), transparent 0deg, var(--vars-node-line-color) 0deg, var(--vars-node-line-color) var(--internal-node-sweep-angle, 0deg), transparent var(--internal-node-sweep-angle, 0deg))"
                    )
                }
        }
    }

    object ConnectionLines {
        sealed interface Kind : ComponentKind
        val Style = CssStyle.base<Kind> {
            val spokeRadius = SiteLayout.Fraction.Half - (Vars.nodeSize.value() + Vars.innerNodeSize.value()) / 2
            Modifier
                .position(Position.Relative)
                .top(SiteLayout.Fraction.Half)
                .left(SiteLayout.Fraction.Half)
                .height(spokeRadius)
        }
        val NoGuideVariant = Style.addVariantBase {
            Modifier.setVariable(Vars.guideLineColor, Colors.Transparent)
        }
        val Guide = CssStyle.base {
            Modifier
                .fillMaxHeight()
                .border(0.px)
                .borderLeft(
                    width = Vars.guideLineWidth.value(),
                    style = Vars.guideLineStyle.value(),
                    color = Vars.guideLineColor.value(
                        Vars.connectingLineColor.value(colorMode.toPalette().border)
                    ),
                )
        }
        val Main = CssStyle.base {
            Modifier
                .fillMaxHeight()
                .border(0.px)
                .borderLeft(
                    width = Vars.connectingLineWidth.value(),
                    style = Vars.connectingLineStyle.value(),
                    color = Vars.connectingLineColor.value(
                        Vars.nodeLineColor.value(colorMode.toPalette().border)
                    ),
                )
        }
    }
}
